import { useCallback, useMemo } from "react";

import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import StyledLoadingButton from "../../../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import { useCreateDevicesTemplate } from "../hooks/useCreateDevicesTemplate";
import { useGetDevicesMakesCached } from "../hooks/useGetDevicesMakesCached";
import {
  IDeviceTemplateForm,
  defaultDeviceTemplateForm,
} from "../variables/deviceTemplateData";
import DeviceTemplateFormItem from "./DeviceTemplateFormItem";

const CreateDeviceTemplate = (): JSX.Element => {
  const { createDevicesTemplate, loading } = useCreateDevicesTemplate();
  const { data, loading: deviceMakesLoading } = useGetDevicesMakesCached();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<IDeviceTemplateForm>({
    defaultValues: defaultDeviceTemplateForm,
    mode: "onSubmit",
  });

  const watchedTemplateData = watch("templateData");

  const watchedNumberOfNodes = watch("numberOfNodes") || 0;

  const deviceTypeOptions = useMemo(() => {
    return (data?.getDevicesMakes?.items ?? [])
      .map(d => d?.model)
      .filter((model): model is string => Boolean(model))
      .sort();
  }, [data]);

  const getModelsToRunForDeviceType = useCallback(
    (deviceType: string): string[] => {
      const matched = data?.getDevicesMakes?.items?.find(
        d => d?.model === deviceType
      );

      return (matched?.modelsToRun ?? []).filter(
        (model): model is string => model !== null
      );
    },
    [data]
  );

  const addDevice = () => {
    const currentDevices = getValues("templateData");

    setValue("templateData", [
      ...currentDevices,
      {
        deviceType: deviceTypeOptions[0] || "",
        nodeNumber: 1,
        deviceIp: "",
        model: [""],
      },
    ]);
  };

  const removeDevice = (indexToRemove: number) => {
    reset(prev => ({
      ...prev,
      templateData: prev.templateData.filter(
        (_d, index) => index !== indexToRemove
      ),
    }));
  };

  const onSubmit = async (data: IDeviceTemplateForm) => {
    if (!data.templateData.length) {
      return;
    }

    await createDevicesTemplate({
      ...data,
      templateData: JSON.stringify(data.templateData),
    });

    reset();
  };

  if (deviceMakesLoading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack marginBottom="1.5em" spacing={2} direction="row">
          <Controller
            name="name"
            control={control}
            rules={{
              required: "This field is required",
            }}
            render={({ field }) => (
              <TextField
                required
                fullWidth
                label="Name"
                {...field}
                value={field.value ?? ""}
                error={errors.name !== undefined}
                helperText={errors.name?.message || ""}
              />
            )}
          />
          <Controller
            name="numberOfNodes"
            control={control}
            rules={{
              required: "This field is required",
              validate: value =>
                value > 0 || "Number of nodes must be greater than 0",
            }}
            render={({ field }) => (
              <TextField
                required
                fullWidth
                label="Number of nodes"
                type="number"
                {...field}
                value={field.value ?? 0}
                error={errors.numberOfNodes !== undefined}
                helperText={errors.numberOfNodes?.message || ""}
              />
            )}
          />
        </Stack>

        <Controller
          name="templateData"
          control={control}
          rules={{
            validate: value => value.length > 0 || "Adding device is required",
          }}
          render={() => <></>} // This controller is just for validation, no UI rendering needed
        />

        <Stack spacing={2} marginBottom={3}>
          {watchedTemplateData.map((device, index) => (
            <DeviceTemplateFormItem
              key={device.deviceType + index}
              index={index}
              device={device}
              deviceTypeOptions={deviceTypeOptions}
              getModelsToRunForDeviceType={getModelsToRunForDeviceType}
              control={control}
              errors={errors}
              onRemove={removeDevice}
              watchedDeviceType={device.deviceType}
              numberOfNodes={watchedNumberOfNodes}
              setValue={setValue}
            />
          ))}
        </Stack>

        <Box mb={3}>
          {errors.templateData && (
            <FormHelperText error>
              <Typography variant="helperText" color="error">
                {errors.templateData.message}
              </Typography>
            </FormHelperText>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={e => {
              e.stopPropagation();

              addDevice();
            }}
          >
            Add Device
          </Button>
          <StyledLoadingButton
            disabled={loading}
            variant="contained"
            type="submit"
          >
            Create
          </StyledLoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default CreateDeviceTemplate;
